/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { Chalk } from "chalk";
import stripAnsi from "strip-ansi";

const chalk = new Chalk({ level: 1 });

// Precompiled regexes for performance
const NEWLINE_REGEX = /\n/g;
const ANSI_UNESCAPE_REGEX = /\\u001b/g;

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogEntry {
	time: string;
	level: LogLevel;
	msg: string;
	data?: any;
}

// Object pool for LogEntry objects to reduce GC pressure in high-frequency scenarios
class LogEntryPool {
	private pool: LogEntry[] = [];
	private readonly maxPoolSize = 50; // Reasonable limit to avoid memory bloat

	get(): LogEntry {
		return this.pool.pop() || { time: "", level: "info", msg: "" };
	}

	release(entry: LogEntry): void {
		if (this.pool.length < this.maxPoolSize) {
			// Clear the object properties
			entry.time = "";
			entry.level = "info";
			entry.msg = "";
			delete entry.data;
			// Clear any dynamic properties
			Object.keys(entry).forEach((key) => {
				if (!["time", "level", "msg"].includes(key)) {
					delete (entry as any)[key];
				}
			});
			this.pool.push(entry);
		}
	}
}

const logEntryPool = new LogEntryPool();

const levelColors = {
	debug: chalk.gray,
	info: chalk.white,
	warn: chalk.yellow,
	error: chalk.red,
};

/**
 * Process string values to remove newlines while preserving other whitespace
 * @param value - The value to process
 * @param seen - WeakSet to track visited objects for circular reference detection
 * @returns Processed value with newlines replaced by spaces
 */
function processStringValue(value: any, seen: WeakSet<object> = new WeakSet()): any {
	if (typeof value === "string") {
		return value.replace(NEWLINE_REGEX, " ");
	}
	if (typeof value === "object" && value !== null) {
		// Handle circular references
		if (seen.has(value)) {
			return "[Circular Reference]";
		}
		seen.add(value);

		const processed: any = {};
		for (const [key, val] of Object.entries(value)) {
			processed[key] = processStringValue(val, seen);
		}
		return processed;
	}
	return value;
}

/**
 * Process JSON string for environment-specific ANSI handling
 * @param jsonString - The JSON string to process
 * @returns Processed string with appropriate ANSI handling
 */
function processAnsiCodes(jsonString: string): string {
	const isProduction = process.env.NODE_ENV === "production";

	// Only remove newlines to keep logs on single lines, preserve all other content spacing
	let processed = jsonString.replace(NEWLINE_REGEX, "");

	if (!isProduction) {
		// Development: Unescape ANSI codes so they render as colors
		processed = processed.replace(ANSI_UNESCAPE_REGEX, "\u001b");
	}

	return processed;
}

/**
 * Reorder object keys according to logging priority:
 * 1. time, 2. level, 3. corrId, 4. userIp, 5. msg, then alphabetically
 */
function reorderLogKeys(obj: any): any {
	if (typeof obj !== "object" || obj === null) {
		return obj;
	}

	const priorityKeys = ["time", "level", "corrId", "userIp", "msg"];
	const orderedObj: any = {};

	// Add priority keys first (if they exist)
	for (const key of priorityKeys) {
		if (key in obj) {
			orderedObj[key] = obj[key];
		}
	}

	// Add remaining keys in alphabetical order (excluding keys already added)
	const remainingKeys = Object.keys(obj)
		.filter((key) => !priorityKeys.includes(key))
		.sort();

	for (const key of remainingKeys) {
		orderedObj[key] = obj[key];
	}

	return orderedObj;
}

function safeStringify(obj: any): string {
	try {
		const orderedObj = reorderLogKeys(obj);
		const result = JSON.stringify(orderedObj);
		return processAnsiCodes(result);
	} catch (error) {
		// Handle circular references and other JSON.stringify errors
		try {
			// Attempt to stringify with a replacer that handles circular references
			const seen = new WeakSet();
			const orderedObj = reorderLogKeys(obj);
			const result = JSON.stringify(orderedObj, (_key, value) => {
				if (typeof value === "object" && value !== null) {
					if (seen.has(value)) {
						return "[Circular Reference]";
					}
					seen.add(value);
				}
				// Handle BigInt
				if (typeof value === "bigint") {
					return value.toString() + "n";
				}
				return value;
			});

			return processAnsiCodes(result);
		} catch (fallbackError) {
			// If all else fails, return a safe error message
			const isProduction = process.env.NODE_ENV === "production";
			const fallbackObj = {
				time: obj.time || new Date().toISOString(),
				level: obj.level || "Unknown",
				error: "Failed to serialize log entry",
				msg: obj.msg || "Unknown",
			};

			// Strip ANSI codes from fallback message in production
			if (isProduction && fallbackObj.msg !== "Unknown") {
				fallbackObj.msg = stripAnsi(fallbackObj.msg);
			}

			const result = JSON.stringify(reorderLogKeys(fallbackObj));
			return processAnsiCodes(result);
		}
	}
}

function log(level: LogLevel, message: string, data: any[] = []): void {
	const colorFn = levelColors[level];
	const isProduction = process.env.NODE_ENV === "production";

	// Get pooled entry for better performance in high-frequency scenarios
	const entry = logEntryPool.get();
	entry.time = new Date().toISOString();
	entry.level = level;
	// Strip ANSI codes in production and remove newlines from message content
	if (isProduction) {
		entry.msg = stripAnsi(message).replace(NEWLINE_REGEX, " ");
	} else {
		if (typeof message?.replace === "function") {
			entry.msg = message.replace(NEWLINE_REGEX, " ");
		} else {
			entry.msg = message || "";
		}
	}

	// Process additional data if present
	if (data !== undefined && data.length) {
		const processedData = data.reduce(
			(prev, curr) => {
				if (typeof curr === "object" && curr !== null) {
					// Process nested object values to remove newlines
					const processedObj = processStringValue(curr);
					return {
						...prev,
						...processedObj,
					};
				}
				// Process primitive values to remove newlines
				prev.args.push(processStringValue(curr));
				return prev;
			},
			{
				args: [] as any[],
			},
		);

		// Only include args field if it has content
		if (processedData.args.length === 0) {
			delete processedData.args;
		}

		// Assign processed data properties to entry
		Object.assign(entry, processedData);
	}

	if (process.env.NEXT_RUNTIME === "nodejs") {
		const jsonString = safeStringify(entry);
		const isProduction = process.env.NODE_ENV === "production";

		// Only apply colors in development to avoid breaking log parsing in production
		const output = isProduction ? jsonString : colorFn(jsonString);
		process.stdout.write(output + "\n");

		// Return entry to pool for reuse
		logEntryPool.release(entry);
	} else {
		console.log(...[message, ...data]);
		// Return entry to pool for reuse
		logEntryPool.release(entry);
	}
}

export const logger = {
	debug: (message: string, ...data: any[]): void => log("debug", message, data),
	info: (message: string, ...data: any[]): void => log("info", message, data),
	log: (message: string, ...data: any[]): void => log("info", message, data),
	warn: (message: string, ...data: any[]): void => log("warn", message, data),
	error: (message: string, ...data: any[]): void => log("error", message, data),
};

// Export pool for potential monitoring/debugging (optional)
export const _internal = {
	logEntryPool,
	// Get current pool size for monitoring
	getPoolSize: () => logEntryPool["pool"].length,
} as const;

export const instrumentation = () => {
	const getLogMethodConsole = (consoleMethod: string) => {
		switch (consoleMethod) {
			case "error":
				return logger.error.bind(logger);
			case "warn":
				return logger.warn.bind(logger);
			case "debug":
			case "trace":
				return logger.debug.bind(logger);
			default:
				return logger.info.bind(logger);
		}
	};

	const consoleMethods = ["log", "debug", "info", "warn", "error", "trace"] as (keyof Console)[];

	consoleMethods.forEach((method) => {
		console[method] = getLogMethodConsole(method) as any;
	});
};
